import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { useCaseIde } from "../context/CaseIdeContext";
import { CompatAPI } from "./api/compatApi";
import type { CompatMigrationResult } from "./types";

export type CompatMigration = {
  /** True while the migrate request is in flight. */
  isUpgrading: boolean;
  /** The last result, once the request has settled. */
  result: CompatMigrationResult | null;
};

const SESSION_KEY = (individualId: string) => `compat:${individualId}`;

const sessionStorageSafe = {
  get(key: string): string | null {
    try {
      return window.sessionStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key: string, value: string) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch {
      // Private mode or a full quota: the server is idempotent anyway.
    }
  }
};

export const FAILED_TOAST_TITLE = "Some legacy items could not be upgraded";
export const FAILED_TOAST_DESCRIPTION =
  "You can keep working; we'll retry next time you open this case.";

const hasFailedStep = (result: CompatMigrationResult): boolean =>
  result.status === "failed" ||
  Object.values(result.steps ?? {}).some((step) => step?.status === "failed");

/**
 * Kick the on-demand legacy migration once per case per browser session, and
 * refresh the trees when it brought anything in. Best effort by contract:
 * a failure is logged, never surfaced as an error state, and the case keeps
 * rendering underneath.
 */
export const useCompatMigration = (individualId: string): CompatMigration => {
  const { invalidate } = useCaseIde();
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [result, setResult] = useState<CompatMigrationResult | null>(null);
  const invalidateRef = useRef(invalidate);
  invalidateRef.current = invalidate;

  useEffect(() => {
    if (!individualId) return undefined;
    const key = SESSION_KEY(individualId);
    if (sessionStorageSafe.get(key) === "complete") return undefined;

    let cancelled = false;
    setIsUpgrading(true);
    setResult(null);

    CompatAPI.migrate(individualId)
      .then((outcome) => {
        if (cancelled) return;
        setResult(outcome);
        if (outcome.status === "complete") {
          sessionStorageSafe.set(key, "complete");
        }
        if (outcome.changed) {
          invalidateRef.current(["fs", "drafts", "research"]);
        }
        if (hasFailedStep(outcome)) {
          toast.message(FAILED_TOAST_TITLE, {
            description: FAILED_TOAST_DESCRIPTION
          });
        }
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.warn("compat migration failed", error);
      })
      .finally(() => {
        if (!cancelled) setIsUpgrading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [individualId]);

  return useMemo(() => ({ isUpgrading, result }), [isUpgrading, result]);
};
